/**
 * The polyfill barrier.
 *
 * ES imports are hoisted, so anything imported at the top of an app's entry
 * evaluates BEFORE the first statement runs. Polyfills must therefore be
 * installed here, and the app reached only through a dynamic import.
 *
 * This module must import nothing but the polyfills subpath. A guard test in
 * __tests__/app-entries.test.ts enforces that. Importing the app-shell barrel
 * here would hoist the whole app graph and defeat the barrier.
 */
import { applyPolyfills } from '@tv2z/platform/utils/polyfills';

/**
 * `Element.closest` is Chrome 41; the Tizen legacy floor is Chrome 38, and
 * neither core-js nor the SDK polyfills supply it (DOM, not ECMA-262). The
 * focus-scroll hooks, Home and ViewAll call it on every focus move. `matches`
 * is Chrome 34; the webkit prefix covers anything older.
 */
export function installElementClosest(proto: Element = Element.prototype): void {
  if (typeof proto.closest === 'function') return;
  const p = proto as Element & { webkitMatchesSelector?: (selector: string) => boolean };
  const matches = p.matches || p.webkitMatchesSelector;
  if (!matches) return;
  const walk = (start: Element, selector: string): Element | null => {
    for (let el: Element | null = start; el && el.nodeType === 1; el = el.parentElement) {
      if (matches.call(el, selector)) return el;
    }
    return null;
  };
  proto.closest = function closest(this: Element, selector: string): Element | null {
    return walk(this, selector);
  } as Element['closest'];
}

/** Install TV polyfills, then load the app. `load` must be a dynamic import. */
export function bootTv(load: () => Promise<unknown>): void {
  applyPolyfills();
  installElementClosest();
  void load();
}
